import { Fragment, useMemo, useRef, useState } from "react";

import { BudgetCells } from "@app/lib/budgets/budget-cells";
import type { categorizationOverviewPage_Query$data } from "@app/queries/categorizationOverviewPage_Query.graphql";
import { CellLink } from "@app/components/ui/cell-link";

export type CostNode =
  categorizationOverviewPage_Query$data["perspectiveCostTree"][number];

const SLICE_DRAG_TYPE = "application/x-cxcash-slice";
const AUTO_EXPAND_MS = 700;

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const compactMoney = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  });

// A cell's share of its column total (the per-month TOTALS row), as a compact
// percent. Returns the em-dash placeholder when there's nothing to show, matching
// the dollar path; one decimal under 10% so small categories don't all read 0%.
const percentOf = (value: number, total: number): string => {
  if (total <= 0 || value <= 0) return "—";
  const pct = (value / total) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
};

// Jan–Dec from the 12 "YYYY-MM" point labels the server returns in order.
const monthLabel = (yyyymm: string) =>
  MONTH_LABELS[Number(yyyymm.slice(5, 7)) - 1] ?? yyyymm;

// The badge that identifies a slice leaf by its waterfall rule.
const sliceBadge = (node: CostNode): string | null => {
  if (node.kind !== "STAKEHOLDER") return null;
  if (node.sliceType === "ABSOLUTE")
    return compactMoney((node.sliceAmountCents ?? 0) / 100);
  if (node.sliceType === "REMAINDER") return "rest";
  return `${Math.round((node.sliceWeight ?? 0) * 100)}%`;
};

// The review grid: the perspective's cost tree as a 12-month grid, reconstructed
// from the flat server nodes via parentNodeId. Totals pinned first, Uncategorized
// last (server order); top-level categories collapsed. onCell drills a month
// cell; onEditSlice opens the split dialog from a slice badge; onSliceDrop
// re-homes a dragged slice — or routes a dragged Uncategorized stakeholder —
// onto a category.
export default function OverviewGrid({
  nodes,
  onCell,
  onEditSlice,
  onSliceDrop,
  budgetMode = false,
  getBudgetText,
  onBudgetChange,
}: {
  nodes: readonly CostNode[];
  onCell?: (node: CostNode, month: string) => void;
  onEditSlice?: (node: CostNode) => void;
  onSliceDrop?: (dragged: CostNode, targetCategory: CostNode) => void;
  // "Actuals vs Planned" mode: each month cell gains an editable budget input and
  // a delta. Drag re-homing is disabled while editing budgets.
  budgetMode?: boolean;
  getBudgetText?: (rowKey: string, period: string) => string;
  onBudgetChange?: (rowKey: string, period: string, text: string) => void;
}) {
  const byParent = useMemo(() => {
    const map = new Map<string | null, CostNode[]>();
    for (const node of nodes) {
      const key = node.parentNodeId ?? null;
      const siblings = map.get(key) ?? [];
      siblings.push(node);
      map.set(key, siblings);
    }
    return map;
  }, [nodes]);
  const byId = useMemo(
    () => new Map(nodes.map((node) => [node.nodeId, node])),
    [nodes]
  );

  // Display mode: dollar amounts (default) or each cell as a % of its month's
  // column total (the TOTALS row). Pure presentation — the dollar values are
  // already in the response, so no refetch; resets on navigation.
  const [showPercent, setShowPercent] = useState(false);
  const totalsNode = useMemo(
    () => nodes.find((node) => node.kind === "TOTALS"),
    [nodes]
  );
  const monthTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const point of totalsNode?.points ?? [])
      map.set(point.month, point.value ?? 0);
    return map;
  }, [totalsNode]);
  const grandTotal = totalsNode?.yearTotal ?? 0;

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (nodeId: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  const expand = (nodeId: string) =>
    setExpanded((previous) => new Set(previous).add(nodeId));

  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  // Hover a collapsed category during a drag to auto-expand it (~700ms).
  const hoverTimer = useRef<{
    nodeId: string;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const clearHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current.timer);
      hoverTimer.current = null;
    }
  };
  const scheduleExpand = (nodeId: string) => {
    if (hoverTimer.current?.nodeId === nodeId || expanded.has(nodeId)) return;
    clearHover();
    hoverTimer.current = {
      nodeId,
      timer: setTimeout(() => expand(nodeId), AUTO_EXPAND_MS),
    };
  };

  const rows = useMemo(() => {
    const out: { node: CostNode; hasChildren: boolean }[] = [];
    const walk = (node: CostNode) => {
      const children = byParent.get(node.nodeId) ?? [];
      out.push({ node, hasChildren: children.length > 0 });
      if (children.length > 0 && expanded.has(node.nodeId))
        for (const child of children) walk(child);
    };
    for (const root of byParent.get(null) ?? []) walk(root);
    return out;
  }, [byParent, expanded]);

  const months = nodes[0]?.points.map((point) => point.month) ?? [];

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
      <table className={`${budgetMode ? "min-w-[2600px]" : "min-w-[1280px]"} w-full text-sm border-collapse`}>
        <thead>
          <tr className="text-xs text-gray-400 uppercase border-b border-gray-200">
            <th className="sticky left-0 z-20 bg-white text-left font-medium px-3 py-2 min-w-[20rem]">
              <div className="flex items-center gap-2">
                <span>Category</span>
                <span
                  className="inline-flex items-center rounded border border-gray-300 overflow-hidden text-[10px] font-semibold"
                  title="Show dollar amounts, or each cell as a share of its month's total"
                >
                  <button
                    type="button"
                    onClick={() => setShowPercent(false)}
                    className={`px-1.5 py-0.5 ${
                      !showPercent
                        ? "bg-gray-900 text-white"
                        : "bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    $
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPercent(true)}
                    className={`px-1.5 py-0.5 ${
                      showPercent
                        ? "bg-gray-900 text-white"
                        : "bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    %
                  </button>
                </span>
              </div>
            </th>
            {months.map((month) =>
              budgetMode ? (
                <th key={month} colSpan={3} className="px-3 py-2 text-right font-medium whitespace-nowrap border-l border-gray-100">
                  <span className="tabular-nums">{monthLabel(month)}</span>
                  <span className="ml-2 text-[10px] normal-case text-gray-400">act · plan · Δ</span>
                </th>
              ) : (
                <th
                  key={month}
                  className="px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap"
                >
                  {monthLabel(month)}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ node, hasChildren }) => (
            <GridRow
              key={node.nodeId}
              node={node}
              hasChildren={hasChildren}
              expanded={expanded.has(node.nodeId)}
              showPercent={showPercent}
              monthTotals={monthTotals}
              grandTotal={grandTotal}
              budgetMode={budgetMode}
              getBudgetText={getBudgetText}
              onBudgetChange={onBudgetChange}
              onToggle={() => toggle(node.nodeId)}
              onCell={onCell}
              onEditSlice={onEditSlice}
              draggable={
                !budgetMode &&
                Boolean(onSliceDrop) &&
                (node.kind === "STAKEHOLDER" ||
                  node.kind === "UNCATEGORIZED_STAKEHOLDER")
              }
              onDragStartSlice={() => setDraggingNodeId(node.nodeId)}
              onDragEndSlice={() => {
                setDraggingNodeId(null);
                clearHover();
              }}
              dragActive={Boolean(draggingNodeId) && node.kind === "CATEGORY"}
              onDragOverCategory={() => scheduleExpand(node.nodeId)}
              onDragLeaveCategory={clearHover}
              onDropSlice={() => {
                clearHover();
                const dragged = draggingNodeId && byId.get(draggingNodeId);
                setDraggingNodeId(null);
                if (dragged && onSliceDrop) onSliceDrop(dragged, node);
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridRow({
  node,
  hasChildren,
  expanded,
  showPercent,
  monthTotals,
  grandTotal,
  budgetMode,
  getBudgetText,
  onBudgetChange,
  onToggle,
  onCell,
  onEditSlice,
  draggable,
  onDragStartSlice,
  onDragEndSlice,
  dragActive,
  onDragOverCategory,
  onDragLeaveCategory,
  onDropSlice,
}: {
  node: CostNode;
  hasChildren: boolean;
  expanded: boolean;
  showPercent: boolean;
  monthTotals: Map<string, number>;
  grandTotal: number;
  budgetMode: boolean;
  getBudgetText?: (rowKey: string, period: string) => string;
  onBudgetChange?: (rowKey: string, period: string, text: string) => void;
  onToggle: () => void;
  onCell?: (node: CostNode, month: string) => void;
  onEditSlice?: (node: CostNode) => void;
  draggable: boolean;
  onDragStartSlice: () => void;
  onDragEndSlice: () => void;
  dragActive: boolean;
  onDragOverCategory: () => void;
  onDragLeaveCategory: () => void;
  onDropSlice: () => void;
}) {
  const isTotals = node.kind === "TOTALS";
  const isTop = node.depth <= 1;
  const indent = Math.max(0, node.depth - 1) * 18;
  const drillable = Boolean(onCell) && node.kind !== "UNCATEGORIZED";
  const badge = sliceBadge(node);
  const [dropHover, setDropHover] = useState(false);
  const isDropTarget = dragActive; // category rows are the valid drop targets

  return (
    <tr
      draggable={draggable}
      onDragStart={
        draggable
          ? (event) => {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(SLICE_DRAG_TYPE, node.nodeId);
              onDragStartSlice();
            }
          : undefined
      }
      onDragEnd={draggable ? onDragEndSlice : undefined}
      onDragOver={
        isDropTarget
          ? (event) => {
              if (!event.dataTransfer.types.includes(SLICE_DRAG_TYPE)) return;
              event.preventDefault();
              setDropHover(true);
              onDragOverCategory();
            }
          : undefined
      }
      onDragLeave={
        isDropTarget
          ? () => {
              setDropHover(false);
              onDragLeaveCategory();
            }
          : undefined
      }
      onDrop={
        isDropTarget
          ? (event) => {
              event.preventDefault();
              setDropHover(false);
              onDropSlice();
            }
          : undefined
      }
      className={`border-b border-gray-100 last:border-b-0 ${
        isTotals
          ? "bg-gray-50 font-semibold text-gray-800"
          : "hover:bg-gray-50/60"
      } ${dropHover ? "ring-2 ring-emerald-400" : ""} ${
        draggable ? "cursor-grab active:cursor-grabbing" : ""
      }`}
    >
      <td
        className={`sticky left-0 z-10 px-3 py-1.5 min-w-[20rem] ${
          isTotals ? "bg-gray-50" : "bg-white"
        }`}
      >
        <div
          className="flex items-center gap-1.5"
          style={{ paddingLeft: `${indent}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={onToggle}
              title={expanded ? "Collapse" : "Expand"}
              className="shrink-0 w-4 text-gray-400 hover:text-gray-700 text-xs"
            >
              {expanded ? "▾" : "▸"}
            </button>
          ) : (
            <span className="shrink-0 w-4" />
          )}
          {node.color && (
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: node.color }}
            />
          )}
          {badge &&
            (onEditSlice ? (
              <button
                type="button"
                onClick={() => onEditSlice(node)}
                title="Edit allocation"
                className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border border-gray-300 text-gray-600 hover:border-gray-500 hover:bg-gray-50 tabular-nums"
              >
                {badge}
              </button>
            ) : (
              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-500 tabular-nums">
                {badge}
              </span>
            ))}
          <span
            className={`truncate ${
              isTop && !isTotals ? "font-medium text-gray-800" : ""
            } ${
              node.kind === "STAKEHOLDER" ||
              node.kind === "UNCATEGORIZED_STAKEHOLDER"
                ? "text-gray-600"
                : ""
            }`}
          >
            {node.name}
          </span>
          <span className="ml-auto pl-3 text-xs text-gray-400 tabular-nums shrink-0">
            {node.yearTotal > 0
              ? showPercent
                ? percentOf(node.yearTotal, grandTotal)
                : money(node.yearTotal)
              : ""}
          </span>
        </div>
      </td>
      {node.points.map((point) => {
        const value = point.value ?? 0;
        const clickable = drillable && value > 0;
        const display = showPercent
          ? percentOf(value, monthTotals.get(point.month) ?? 0)
          : money(value);
        return (
          <Fragment key={point.month}>
            <td
              className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${
                budgetMode ? "border-l border-gray-100" : ""
              }`}
            >
              {value <= 0 ? (
                <span className="text-gray-300">—</span>
              ) : clickable ? (
                <CellLink onClick={() => onCell?.(node, point.month)} title="View transactions for this month">
                  {display}
                </CellLink>
              ) : (
                display
              )}
            </td>
            {budgetMode && getBudgetText && onBudgetChange && (
              <BudgetCells
                actual={value}
                text={getBudgetText(node.nodeId, point.month)}
                onChange={(text) => onBudgetChange(node.nodeId, point.month, text)}
                side="COST"
              />
            )}
          </Fragment>
        );
      })}
    </tr>
  );
}
